<script setup>
import { useInsightsStore } from "@/components/AgreementEditor/stores/insights-store.js";
import { useSuperdoc } from '@/composables/superdoc/use-superdoc.js';
import { createSuperdocDraft, DraftStates } from '@/domain/drafts/draft';
import { useLinkObject } from '@/pages/Dashboard/composables/LinkObject';
import { useDraftsStore } from '@/pages/Drafts/stores/drafts-store';
import { useSettingsPageStore } from '@/pages/Settings/stores/settings-store';
import { useTemplatesStore } from '@/pages/Templates/stores/templates-store';
import { SuperdocService } from '@/services/superdoc-services';
import { useHarbourStore } from '@/stores/harbour-store';
import { useSuperdocStore } from '@/stores/superdoc-store';
import { publishEvent } from '@/utils/bus';
import { BlankDOCX, DOCX, getFileObject } from '@harbour-enterprises/superdoc';
import { HarbourSuperdoc } from '@static/superdoc/dist/harbour-superdoc.es.js';
import '@static/superdoc/dist/style.css';
import axios from 'axios';
import { ToastProgrammatic as Toast } from 'buefy';
import Vue, { getCurrentInstance, isRef, onBeforeUnmount, onMounted, ref } from 'vue';
const { proxy } = getCurrentInstance();
const draftsStore = useDraftsStore();
const harbourStore = useHarbourStore();
const insightsStore = useInsightsStore();
const superdocStore = useSuperdocStore();
const templatesStore = useTemplatesStore();
const settingsStore = useSettingsPageStore();

const emit = defineEmits(['close']);
const props = defineProps({
  file: {
    type: File,
    required: false,
  },
  superdocId: {
    type: String,
    required: false,
  },
  isBlankDocument: {
    type: Boolean,
    required: false,
  },
  superdocName: {
    type: String,
    required: false,
  },
  superdocPromise: {
    type: Promise,
    required: false,
  },
  contextDict: {
    type: Object,
    required: true,
  },
  isStandalone: {
    type: Boolean,
    required: false,
    default: false,
  },
  isAIUpload: {
    type: Boolean,
    required: false,
    default: false,
  },
  parent: {
    type: Object,
    required: false,
  },
  mode: {
    type: String,
    required: false,
  },
  template: {
    type: Object,
    required: false,
  },
  draft: {
    type: Object,
    required: false,
  },
  agreementId: {
    type: String,
    required: false,
  },
  linkId: {
    type: String,
    required: false,
  },
  linkObject: {
    type: Object,
    required: false,
  },
  referenceAttachments: {
    type: Array,
    required: false,
    default: () => [],
  },
  superdocProp: {
    type: Object,
    required: false,
  },
  templateGroupId: {
    type: String,
  },
  editorProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  agreementEditorPrevFlow: {
    type: Boolean,
    default: false,
  },
  previewFile: {
    type: File,
    required: false,
  },
});

let superdocModalInstance;
const linkObject = ref(props.linkObject || null);
const windowWidth = ref(window.innerWidth);
const windowHeight = ref(window.innerHeight);

const currentFile = ref(props.file || null);

superdocStore.sourceFile = props.previewFile || currentFile;

const onNewSuperdoc = (superdoc) => {
  const draft = createSuperdocDraft(superdoc, {
    systemEmail: harbourStore.contextDict?.systememail,
  });
  publishEvent('drafts:created', draft);
};

const onCollaborationReady= (superdocId) => {

  // When collaboration ready start AI processing
  if(props.isAIUpload && !insightsStore.isProcessingStarted) {
    if(!insightsStore.isActiveResults({draftId: superdocId})) {
      insightsStore.stopPreviousStreaming();
    }
    insightsStore.getAiInsights({draftId: superdocId});
  }
};

const updateWindowSize = () => {
  windowWidth.value = window.innerWidth;
  windowHeight.value = window.innerHeight;
};

const currentScale = ref(1);
const editorElem = ref(null);

/**
 * @description Scale editor to fit the screen.
 * Editor element is expected to be initialized.
 * @returns {void}
 */
const scaleEditorToFit = () => {
  if (!editorElem.value) return;

  // get screen size
  const screeenWidth = document.documentElement.clientWidth;
  // get document size
  let docWidth = editorElem.value.clientWidth;
  if (screeenWidth >= docWidth) return;

  const widthPercentage = (screeenWidth / docWidth) * .9;
  currentScale.value = widthPercentage;

  editorElem.value.parentElement.style.transform = `scale(${currentScale.value})`;
  editorElem.value.parentElement.style.transformOrigin = '0 0';
}

/**
 * If in standalone mode, we won't have a linkObject passed in so we use this to fetch it.
 */
const getLinkDataDetails = async () => {
  const { isStandalone, superdocId, linkId } = props;
  if (!superdocId || (!isStandalone && !linkId)) return;
  const url = '/data?agreebuilder-getmylinkdatadetails';
  const payload = {
    requesttype: 'agreebuilder-getmylinkdatadetails',
    agreedisplayid: props.linkId,
    documentid: props.superdocId,
    ckeditorfileid: null,
  };
  const res = await axios.post(url, payload, null);
  if (!res.data) {
    console.error('Error fetching link data details');
    return null;
  }

  const { linkdata } = res.data;
  const customInputs = linkdata.client_importbyurl_custominputvaluesjson || {};
  linkObject.value = useLinkObject({
    custom_inputs: customInputs,
    last_update: linkdata.client_importbyurl_lastupdatecreatedatetime,
  });
}

const onEditorCreate = (editor) => {
  if (linkObject.value) {
    const customInputs = linkObject?.value?.customInputs.custom_inputs || linkObject?.value?.customInputs.custominputs || [];
    superdocStore.updateAgreementOwnerAnnotations({
      editor: editor,
      source: 'dashboard',
      options: {
        isRemoveConditionals: false,
        customInputs,
        hiddenConditionalInputs: linkObject?.value?.customInputs.hiddenConditionalInputs || [],
        referenceAttachments: props.referenceAttachments,
      },
    });
  }
  editorElem.value = document.querySelector('.super-editor-container');
  setTimeout(scaleEditorToFit, 1000);
};

const loadBlankFile = async () => {
  let currentDate = superdocStore.getFormattedDate(null, 'mm.dd.yyyy');
  let name = `Untitled - ${currentDate}`;

  return await getFileObject(
    BlankDOCX,
    name,
    DOCX,
  );
};

/**
 * Open the full screen superdoc experience (harbour-superdoc). This will render
 * the Vue3 superdoc modal using SuperdocViewer component.
 *
 * @param file Optional file to initialize with
 */
const init = async (file) => {
  if (props.isBlankDocument) file = await loadBlankFile();
  currentFile.value = file;

  if (!settingsStore.emailAutocompleteData.length) {
    await settingsStore.loadAllUsersData();
  }


  let orgWWW = harbourStore.contextDict?.org_www;
  if (!orgWWW) {
    const contextResp = await fetch('/context');
    const context = await contextResp.json();
    orgWWW = context.org_www;
  }

  let currentOrgDomain = orgWWW || '';
  const regex = /^(?:https?:\/\/)?(?:www\.)?([^\/\n?]+)/;
  const match = currentOrgDomain.match(regex);
  currentOrgDomain = match ? match[1] : null;

  let mode = props.mode;
  if (!mode && props.linkId) {
    mode = 'agreement';
  }

  superdocModalInstance = new HarbourSuperdoc({
    elementId: 'sd-editor-container-superdoc',
    file,
    environment: import.meta.env.VITE_ENV,
    superdocId: props.superdocId,
    agreementId: props.agreementId,
    annotations: true,
    linkId: props.linkId || props.editorProps.linkDisplayId,
    superdocName: props.superdocName,
    context: props.contextDict,
    isStandalone: props.isStandalone,
    mode,
    linkObject: linkObject.value,
    superdocProp: props.superdocProp,
    agreementEditorPrevFlow: props.agreementEditorPrevFlow,
    templatesGroups: templatesStore.templatesGroups,
    isAIUpload: props.isAIUpload,
    onNewSuperdoc,
    onTitleChange,
    onDocumentShared,
    onUserRemoved,
    onContinueToAE,
    onPrepareBookmark,
    onCopyAndContinueToAE,
    onEditorCreate,
    onCollaborationReady,
    emailAutocompleteData: settingsStore.emailAutocompleteData,
    userGroups: settingsStore.listUserGroups,
    currentOrgDomain
  });

  superdocModalInstance.on('close', (params) => handleModalClose(params));
};

const handleModalClose = (params = {}) => {
  harbourStore.resetPrepareForBookmark();
  if (params.isUpdateLink) {
    const message = params.linkName ? `"${params.linkName}" updated successfully` : 'Link updated successfully';
    Toast.open({
      message,
      type: 'is-success',
      position: 'is-top',
      duration: 5000,
    });
  }
  emit('close');
};

const onDocumentShared = ({ id, collaborators }) => {
  draftsStore.onDocumentShared({ id, collaborators });
};

const onUserRemoved = ({ id, user }) => {
  draftsStore.onRemoveCollaborator({ id, user });
};

const onContinueToAE = async (superdoc) => {
  harbourStore.resetPrepareForBookmark();
  const templateModes = [DraftStates.TemplateDraft, DraftStates.Template];
  const creationMode = templateModes.includes(superdoc.state) ? DraftStates.Template : 'agreement';

  const agreementId = isRef(superdoc.agreement?.id) ? superdoc.agreement?.id?.value : superdoc.agreement?.id;
  const agreementEditorProps = {
    ...props.editorProps,
    parent: proxy,
    creationMode,
    superdoc,
    agreementId: props.agreementId || agreementId,
    linkDisplayId: props.linkId || superdoc.link?.id?.value || props.editorProps.linkDisplayId,
    fullScreen: true,
  };

  if (props.template) {
    agreementEditorProps.agreementId = props.template.agreement_id;
    agreementEditorProps.templateGroupId = props.template.template_group_id;

    if (props.templateGroupId && creationMode === 'template') {
      agreementEditorProps.templateGroupId = props.templateGroupId;
    }
  }

  const superdocTemplateGroupId = isRef(superdoc.agreement?.templateGroupId) ? superdoc.agreement?.templateGroupId.value : superdoc.agreement?.templateGroupId;
  if (superdocTemplateGroupId) {
    agreementEditorProps.templateGroupId = superdocTemplateGroupId;
  }

  if (superdoc.state === DraftStates.Template && props.draft) {
    agreementEditorProps.creationMode = 'agreement';
    agreementEditorProps.sourceDraftId = props.draft.id;
  }

  Vue.prototype.$openAgreementEditorModal({ props: agreementEditorProps });

  if (!props.isStandalone) {
    emit('close');
  }
};

const onPrepareBookmark = ({ id, title }) => {
  harbourStore.prepareForSuperdocBookmark({ title, id });
};

const onCopyAndContinueToAE = async ({
  superdocId,
  agreementId,
  templateGroupId,
  signers,
  shouldCopy
}) => {
  let superdocData = null;
  let superdoc = null;

  const creatingToast = Toast.open({
    message: 'Creating template...',
    type: 'is-success',
    position: 'is-top',
    indefinite: true,
  });

  if (shouldCopy) {
    const response = await templatesStore.copyDocument({
      templateId: agreementId,
      fileDisplayId: null,
      isTemplate: true,
      addCopyToTitle: true,
      templateGroupId
    })
    const { copiedagreementid, new_superdoc_id } = response;

    superdocData = await SuperdocService.getDocument(new_superdoc_id);
    superdoc = useSuperdoc(superdocData);
    superdoc.agreement.id = copiedagreementid;
  } else {
    superdocData = await SuperdocService.getDocument(superdocId);
    superdoc = useSuperdoc(superdocData);
  }

  await SuperdocService.updateDocument(
    superdoc.id,
    { state: 'template-draft' }
  );

  // state will be used to determine creation mode by AE
  superdoc.state = 'template-draft';
  superdoc.agreement.templateGroupId = templateGroupId;
  superdoc.agreement.signers = signers;

  superdoc.setInitialized();

  creatingToast.close();
  handleModalClose();
  onContinueToAE(superdoc);
}

const onTitleChange = ({ id, title }) => {
  onPrepareBookmark({ id, title });
  if (props.isStandalone) {
    draftsStore.updateDocumentTitle({ id, name: title });
  } else {
    draftsStore.renameDraft(id, title);
  }
};

onMounted(async () => {
  if (!linkObject.value) {
    await getLinkDataDetails();
  }

  init(props.file);
  window.addEventListener('resize', updateWindowSize);
});

onBeforeUnmount(() => {
  harbourStore.resetPrepareForBookmark();
  window.removeEventListener('resize', updateWindowSize);
});
</script>

<template>
  <div class="sd-editor-container">
    <div class="sd-editor-container__main hrbr-superdoc">
      <div class="sd-editor-container__superdoc"
        id="sd-editor-container-superdoc">
      </div>
    </div>
  </div>
</template>

<style>
.sd-editor-container__main button.modal-close>* {
  color: black !important;
  display: none;
}

.sd-editor-container__main button.modal-close.is-large::before,
.sd-editor-container__main button.modal-close.is-large::after {
  color: black !important;
}
</style>

<style lang="postcss" scoped>
.sd-editor-container {
  display: grid;
  width: 100%;
  height: 100vh;

  @supports (height: 100dvh) {
    height: 100dvh;
  }

  &__main {
    display: grid;
    width: 100%;
    height: 100%;
  position: relative;
    background-color: #fff;
}

  &__superdoc {
    display: grid;
  width: 100%;
  height: 100%;
  }
}
</style>
